import { c as _c } from "react/compiler-runtime";
import { Editor } from '@monaco-editor/react';
import { cx } from 'class-variance-authority';
import type * as monacoTypes from 'monaco-editor';
import { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { getMonacoConfig, Shimmer } from '../..';
import useIsDarkMode from '../../hooks/use-is-dark-mode';
import { useMonaco } from '../monaco/monaco-provider';
import { getJsonThemeName } from '../template-editor/utils/monaco-config';
import type { TemplateInputProps } from '../template-input/template-input';
import { createTemplateCompletionProvider, setupAutoTrigger } from './template-autocomplete';
const MIN_HEIGHTS = {
  medium: 160,
  large: 328
} as const;
const MAX_HEIGHT = 656;
export type JsonEditorProps = {
  value?: string;
  onChange?: (val: string) => void;
  isDisabled?: boolean;
  placeholder?: string;
  /**
   * The height the editor starts at. It grows with its content up to 656px, then scrolls.
   */
  size?: keyof typeof MIN_HEIGHTS;
  /**
   * Fixes the height instead, for an editor that fills a container it measures.
   */
  height?: number;
  onFocus?: () => void;
  onBlur?: () => void;
  /**
   * If provided, enables template variable autocomplete with {{ }} syntax.
   * Template variables will be suggested when typing {{ in the editor.
   */
  autocompleteItems?: TemplateInputProps['autocompleteItems'];
  /**
   * Callback fired when Monaco's validation changes.
   * Returns validation markers (errors/warnings) from Monaco.
   */
  onValidate?: (markers: monacoTypes.editor.IMarker[]) => void;

  /**
   * JSON schema to validate the editor content.
   */
  schemas?: monacoTypes.languages.json.DiagnosticsOptions['schemas'];
  allowTrailingCommas?: boolean;
  /**
   * Disables JSON syntax validation and squiggly error lines.
   * Useful when we don't want to enforce proper JSON.
   */
  disableJsonSchemaValidation?: boolean;
};
export const JsonEditor = forwardRef<HTMLDivElement, JsonEditorProps>((t0, ref) => {
  const $ = _c(50);
  const {
    value: t1,
    onChange,
    isDisabled: t2,
    autocompleteItems,
    placeholder,
    onValidate,
    schemas,
    size: t3,
    height,
    onFocus,
    onBlur,
    allowTrailingCommas,
    disableJsonSchemaValidation: t4
  } = t0;
  const value = t1 === undefined ? "" : t1;
  const isDisabled = t2 === undefined ? false : t2;
  const size = t3 === undefined ? "large" : t3;
  const disableJsonSchemaValidation = t4 === undefined ? false : t4;
  const [contentHeight, setContentHeight] = useState(0);
  const editorHeight = height ?? Math.min(Math.max(contentHeight, MIN_HEIGHTS[size]), MAX_HEIGHT);
  const [isEditorMounted, setIsEditorMounted] = useState(false);
  const [editorLanguage, setEditorLanguage] = useState("json");
  const {
    monaco
  } = useMonaco();
  const isDarkMode = useIsDarkMode();
  let t5;
  if ($[0] !== isDarkMode) {
    t5 = getJsonThemeName(isDarkMode);
    $[0] = isDarkMode;
    $[1] = t5;
  } else {
    t5 = $[1];
  }
  const themeName = t5;
  let t6;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = [];
    $[2] = t6;
  } else {
    t6 = $[2];
  }
  const disposables = useRef(t6);
  const editorRef = useRef(null);
  let t7;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = [];
    $[3] = t7;
  } else {
    t7 = $[3];
  }
  const decorationsRef = useRef(t7);
  let t8;
  let t9;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = () => ({
      focus: () => {
        editorRef.current?.focus();
      },
      blur: () => {
        editorRef.current?.getDomNode()?.blur();
      }
    }) as HTMLDivElement;
    t9 = [];
    $[4] = t8;
    $[5] = t9;
  } else {
    t8 = $[4];
    t9 = $[5];
  }
  useImperativeHandle(ref, t8, t9);
  let t10;
  if ($[6] !== allowTrailingCommas || $[7] !== disableJsonSchemaValidation || $[8] !== monaco || $[9] !== onValidate || $[10] !== schemas || $[11] !== themeName) {
    t10 = editor => {
      editorRef.current = editor;
      editor.updateOptions({
        theme: themeName
      });
      if (monaco?.languages?.json) {
        monaco.languages.json.jsonDefaults.setDiagnosticsOptions({
          validate: !disableJsonSchemaValidation,
          schemaValidation: disableJsonSchemaValidation ? "ignore" : "error",
          schemaRequest: "warning",
          allowComments: true,
          trailingCommas: allowTrailingCommas ? "ignore" : "error",
          schemas
        });
        monaco.languages.json.jsonDefaults.setModeConfiguration({
          documentFormattingEdits: true,
          documentRangeFormattingEdits: true,
          completionItems: Boolean(schemas),
          hovers: Boolean(schemas),
          documentSymbols: false,
          tokens: true,
          colors: false,
          foldingRanges: false,
          diagnostics: !disableJsonSchemaValidation,
          selectionRanges: false
        });
      }
      if (onValidate && monaco) {
        const model = editor.getModel();
        if (model) {
          const updateDecorations = markers => {
            const newDecorations = markers.map(marker => ({
              range: new monaco.Range(marker.startLineNumber, marker.startColumn, marker.endLineNumber, marker.endColumn),
              options: {
                className: "json-error-decoration",
                inlineClassName: "json-error-inline"
              }
            }));
            decorationsRef.current = editor.deltaDecorations(decorationsRef.current, newDecorations);
            onValidate(markers);
          };
          const markers_0 = monaco.editor.getModelMarkers({
            resource: model.uri
          });
          updateDecorations(markers_0);
          const markerListener = monaco.editor.onDidChangeMarkers(() => {
            const updatedMarkers = monaco.editor.getModelMarkers({
              resource: model.uri
            });
            updateDecorations(updatedMarkers);
          });
          disposables.current.push(markerListener);
        }
      }
      const langChange = editor.onDidChangeModelLanguage(e => setEditorLanguage(e.newLanguage));
      disposables.current.push(langChange);
      setContentHeight(editor.getContentHeight());
      disposables.current.push(editor.onDidContentSizeChange(e_0 => setContentHeight(e_0.contentHeight)));
      setIsEditorMounted(true);
    };
    $[6] = allowTrailingCommas;
    $[7] = disableJsonSchemaValidation;
    $[8] = monaco;
    $[9] = onValidate;
    $[10] = schemas;
    $[11] = themeName;
    $[12] = t10;
  } else {
    t10 = $[12];
  }
  const onMount = t10;
  let t11;
  let t12;
  if ($[13] !== autocompleteItems || $[14] !== editorLanguage || $[15] !== isEditorMounted || $[16] !== monaco) {
    t11 = () => {
      if (!isEditorMounted || !autocompleteItems || !monaco || !editorRef.current) {
        return;
      }
      const provider = createTemplateCompletionProvider(monaco, autocompleteItems);
      const providerDisp = monaco.languages.registerCompletionItemProvider(editorLanguage, provider);
      const autoTriggerDisp = setupAutoTrigger(editorRef.current);
      return () => {
        providerDisp.dispose();
        autoTriggerDisp.dispose();
      };
    };
    t12 = [autocompleteItems, monaco, editorLanguage, isEditorMounted];
    $[13] = autocompleteItems;
    $[14] = editorLanguage;
    $[15] = isEditorMounted;
    $[16] = monaco;
    $[17] = t11;
    $[18] = t12;
  } else {
    t11 = $[17];
    t12 = $[18];
  }
  useEffect(t11, t12);
  let t13;
  let t14;
  if ($[19] !== themeName) {
    t13 = () => {
      if (editorRef.current) {
        editorRef.current.updateOptions({
          theme: themeName
        });
      }
    };
    t14 = [themeName];
    $[19] = themeName;
    $[20] = t13;
    $[21] = t14;
  } else {
    t13 = $[20];
    t14 = $[21];
  }
  useEffect(t13, t14);
  let t15;
  let t16;
  if ($[22] === Symbol.for("react.memo_cache_sentinel")) {
    t15 = () => () => {
      disposables.current.forEach(_temp);
      disposables.current = [];
    };
    t16 = [];
    $[22] = t15;
    $[23] = t16;
  } else {
    t15 = $[22];
    t16 = $[23];
  }
  useEffect(t15, t16);
  if (!monaco) {
    let t17;
    if ($[24] !== editorHeight) {
      t17 = <Shimmer className="w-full" height={editorHeight} />;
      $[24] = editorHeight;
      $[25] = t17;
    } else {
      t17 = $[25];
    }
    return t17;
  }
  const t17 = !isEditorMounted;
  let t18;
  if ($[26] !== onBlur || $[27] !== onFocus) {
    t18 = {
      onFocus,
      onBlur
    };
    $[26] = onBlur;
    $[27] = onFocus;
    $[28] = t18;
  } else {
    t18 = $[28];
  }
  let t19;
  if ($[29] !== isDisabled) {
    t19 = cx("\n          relative w-full rounded-md\n          bg-secondary outline-none\n          px-3\n          [&_.monaco-editor]:absolute!\n          [&_.monaco-editor-background]:bg-secondary!\n          [&_.margin-view-overlays]:bg-secondary!\n          [&_.sticky-line-content]:bg-secondary!\n          [&_.sticky-line-number]:bg-secondary!\n          [&_.json-error-inline]:underline!\n          [&_.json-error-inline]:decoration-wavy!\n          [&_.json-error-inline]:decoration-1!\n          [&_.json-error-inline]:[text-decoration-color:#ef4444]!\n          [&_.squiggly-error]:hidden!\n          [&_.squiggly-d-error]:hidden!\n          [&_.zone-widget]:hidden!\n          [&_.findMatch]:bg-transparent!\n          [&_.ime-input]:bg-transparent!\n        ", {
      "bg-secondary! border-tertiary! opacity-60": isDisabled
    });
    $[29] = isDisabled;
    $[30] = t19;
  } else {
    t19 = $[30];
  }
  const t20 = `${editorHeight}px`;
  let t21;
  if ($[31] !== onChange) {
    t21 = v => onChange?.(v ?? "");
    $[31] = onChange;
    $[32] = t21;
  } else {
    t21 = $[32];
  }
  const t22 = Boolean(schemas);
  let t23;
  if ($[33] !== isDisabled || $[34] !== placeholder || $[35] !== schemas || $[36] !== t22) {
    t23 = getMonacoConfig({
      formatOnPaste: true,
      formatOnType: true,
      lineNumbers: "on",
      cursorStyle: "line",
      cursorWidth: 2,
      cursorBlinking: "blink",
      quickSuggestions: false,
      suggestOnTriggerCharacters: true,
      acceptSuggestionOnEnter: "on",
      hover: {
        enabled: t22
      },
      suggest: {
        showWords: false,
        showSnippets: false,
        showVariables: true,
        showKeywords: false,
        showFunctions: false,
        showClasses: false,
        showMethods: false,
        showProperties: Boolean(schemas),
        localityBonus: true,
        filterGraceful: false,
        snippetsPreventQuickSuggestions: false,
        showInlineDetails: true,
        preview: true
      },
      padding: {
        top: 12,
        bottom: 12
      },
      lineDecorationsWidth: 12,
      lineHeight: 20,
      lineNumbersMinChars: 0,
      placeholder,
      size: "default",
      disabled: isDisabled,
      fontSize: 13,
      fontFamily: "\"DM Mono\", \"SFMono-Regular\", Consolas, \"Liberation Mono\", Menlo, Courier, monospace",
      scrollbar: {
        alwaysConsumeMouseWheel: false
      }
    });
    $[33] = isDisabled;
    $[34] = placeholder;
    $[35] = schemas;
    $[36] = t22;
    $[37] = t23;
  } else {
    t23 = $[37];
  }
  let t24;
  if ($[38] === Symbol.for("react.memo_cache_sentinel")) {
    t24 = <Shimmer className="w-full h-full" />;
    $[38] = t24;
  } else {
    t24 = $[38];
  }
  let t25;
  if ($[39] !== onMount || $[40] !== t18 || $[41] !== t19 || $[42] !== t20 || $[43] !== t21 || $[44] !== t23 || $[45] !== value) {
    t25 = <Editor wrapperProps={t18} className={t19} height={t20} language="json" value={value} onChange={t21} options={t23} onMount={onMount} loading={t24} />;
    $[39] = onMount;
    $[40] = t18;
    $[41] = t19;
    $[42] = t20;
    $[43] = t21;
    $[44] = t23;
    $[45] = value;
    $[46] = t25;
  } else {
    t25 = $[46];
  }
  let t26;
  if ($[47] !== t17 || $[48] !== t25) {
    t26 = <div className="overflow-visible w-full relative" aria-busy={t17}>{t25}</div>;
    $[47] = t17;
    $[48] = t25;
    $[49] = t26;
  } else {
    t26 = $[49];
  }
  return t26;
});
JsonEditor.displayName = 'JsonEditor';
function _temp(d) {
  d.dispose();
}